import ComparisonCard from './ComparisonCard';

interface ComparisonData {
  current: {
    period: string;
    data: {
      totalRevenue?: number;
      totalOrders?: number;
      avgOrderValue?: number;
      totalDiscounts?: number;
      count?: number;
      [key: string]: number | undefined;
    };
  };
  previous: {
    period: string;
    data: {
      totalRevenue?: number;
      totalOrders?: number;
      avgOrderValue?: number;
      totalDiscounts?: number;
      count?: number;
      [key: string]: number | undefined;
    };
  };
  changes: Record<string, { value: number; change: number; direction: 'up' | 'down' | 'stable' }>;
}

interface ComparisonGridProps {
  data: ComparisonData;
  expanded?: boolean;
}

/**
 * Grid of KPI comparison cards for period-over-period analysis
 * Best practice: Each metric gets its own card with its own context
 */
export default function ComparisonGrid({ data, expanded = false }: ComparisonGridProps) {
  const { current, previous, changes } = data;
  const currentData = current.data || {};
  const previousData = previous.data || {};

  // Build metrics array based on available data
  const metrics: {
    key: string;
    label: string;
    format: 'currency' | 'number' | 'percentage';
  }[] = [];

  if ('totalRevenue' in currentData || 'totalRevenue' in previousData) {
    metrics.push({ key: 'totalRevenue', label: 'Total Revenue', format: 'currency' });
  }
  if ('totalOrders' in currentData || 'totalOrders' in previousData) {
    metrics.push({ key: 'totalOrders', label: 'Total Orders', format: 'number' });
  }
  if ('avgOrderValue' in currentData || 'avgOrderValue' in previousData) {
    metrics.push({ key: 'avgOrderValue', label: 'Average Order Value', format: 'currency' });
  }
  if ('totalDiscounts' in currentData || 'totalDiscounts' in previousData) {
    metrics.push({ key: 'totalDiscounts', label: 'Total Discounts', format: 'currency' });
  }
  if ('count' in currentData || 'count' in previousData) {
    metrics.push({ key: 'count', label: 'Count', format: 'number' });
  }

  // Format period labels for display
  const formatPeriod = (period: string) => {
    if (period === 'this_week') return 'This Week';
    if (period === 'last_week') return 'Last Week';
    if (period === 'this_month') return 'This Month';
    if (period === 'last_month') return 'Last Month';
    if (period === '7d') return 'Last 7 Days';
    if (period === '30d') return 'Last 30 Days';
    return period;
  };

  const gridCols = expanded
    ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
    : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3';

  return (
    <div className="space-y-3">
      {/* Period Labels */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="font-medium">{formatPeriod(current.period)}</span>
        <span>compared to {formatPeriod(previous.period)}</span>
      </div>

      {/* Metrics Grid */}
      <div className={`grid ${gridCols} gap-3`}>
        {metrics.map((metric) => {
          const currentVal = currentData[metric.key] ?? 0;
          const previousVal = previousData[metric.key] ?? 0;
          const change = changes[metric.key]?.change ?? 0;

          return (
            <ComparisonCard
              key={metric.key}
              label={metric.label}
              currentValue={currentVal}
              previousValue={previousVal}
              changePercent={change}
              format={metric.format}
              size={expanded ? 'md' : 'sm'}
            />
          );
        })}
      </div>

      {/* Summary insight */}
      <div className="text-xs text-muted-foreground text-center pt-2 border-t">
        {changes.totalRevenue?.direction === 'down' ? (
          <span className="text-red-600 dark:text-red-400">
            Revenue declined by {Math.abs(changes.totalRevenue.change).toFixed(1)}% compared to the previous period
          </span>
        ) : changes.totalRevenue?.direction === 'up' ? (
          <span className="text-green-600 dark:text-green-400">
            Revenue increased by {changes.totalRevenue.change.toFixed(1)}% compared to the previous period
          </span>
        ) : (
          <span>Revenue remained stable compared to the previous period</span>
        )}
      </div>
    </div>
  );
}
